import { useState, useEffect, useRef } from "react";
import "../styles/LiveTranscript.css";
import { ReactComponent as Copy } from "../assets/copy.svg";
import { ReactComponent as Clear } from "../assets/reset.svg";
import useAutoScroll from "../hooks/useAutoScroll";
import useKeywordManagement from "../hooks/useKeywordManagement";
import useToggleTranscription from "../hooks/useToggleTranscription";
import alert from "../assets/alert.mp3";
const alarm = new Audio(alert);

interface LiveTranscriptProps {
  transcription: string[];
  interimTranscription: string;
  handleCopyToClipboard: () => void;
  handleClearText: () => void;
  isActive: boolean;
}

export default function LiveTranscript({
  transcription,
  interimTranscription,
  handleCopyToClipboard,
  handleClearText,
  isActive,
}: LiveTranscriptProps) {
  const { keywords, newKeyword, setNewKeyword, addKeyword, removeKeyword } =
    useKeywordManagement();
  const transcriptionRef = useAutoScroll([transcription]);
  const { isTranscriptionActive } = useToggleTranscription();

  const [timer, setTimer] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isTranscriptionActive) {
      timerRef.current = setInterval(() => {
        setTimer((prevTimer) => prevTimer + 1);
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isTranscriptionActive]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isActive) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer + 1);
      }, 1000);
    } else if (!isActive && interval !== null) {
      clearInterval(interval);
    }

    return () => {
      if (interval !== null) {
        clearInterval(interval);
      }
    };
  }, [isActive]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const escapeRegex = (keyword: string) => {
    return keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escapes special characters
  };

  const highlightKeywords = (text: string) => {
    let result = text;
    keywords.forEach((keyword) => {
      const escapedKeyword = escapeRegex(keyword);
      const regex = new RegExp(escapedKeyword, "gi");
      result = result.replace(
        regex,
        `<span class="highlight">${keyword}</span>`
      );
    });
    return `${result}`;
  };

  // Function to check for keywords and play alert sound
  const checkForKeywordsAndAlert = (text: string) => {
    if (!text || !keywords.length) return;

    const regex = new RegExp(`(${keywords.join("|")})`, "gi");
    if (regex.test(text)) {
      alarm.play();
    }
  };

  // Check for keywords in interim
  useEffect(() => {
    checkForKeywordsAndAlert(interimTranscription);
  }, [interimTranscription]);

  return (
    <div className="live-transcript">
      <div
        className="live-text"
        style={{ color: interimTranscription ? "#111827" : "#808080" }}
      >
        {interimTranscription ? interimTranscription : "Live Transcript"}
      </div>
      <div className="transcript-content">
        <div className="transcript-header">
          <span className="time-label">Time</span>
          <span className="time-value">{formatTime(timer)}</span>
          <div className="action-buttons">
            <button onClick={handleCopyToClipboard} className="action-button">
              <Copy />
            </button>
            <button
              onClick={() => {
                handleClearText();
                setTimer(0);
              }}
              className="action-button"
            >
              <Clear />
            </button>
          </div>
        </div>
        <div
          className="transcript-text"
          ref={transcriptionRef}
          dangerouslySetInnerHTML={{
            __html:
              transcription.length > 0
                ? transcription
                    .map((item, index) => highlightKeywords(item))
                    .join("")
                : "<p style='color:rgb(128, 128, 128); margin-top: 0px'>Final transcript will appear here.</p>",
          }}
        ></div>
      </div>
    </div>
  );
}
